import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Smile, Frown } from 'lucide-react';
import EmotionPieChart, { getEmotionColor } from '../EmotionPieChart';
import { useAppSettings } from '../providers/AppSettingsProvider';
import { getDateKey, getWeekStartMonday } from '../../services/journalService';
import { valenceToMoodChart01, type EmotionDetectionResponse } from '../../services/exploreService';
import { translateInsightEmotionLabel } from '../../config/insightEmotions';

const CHART_W = 300;
const CHART_H = 160;
const PAD_L = 8;
const PAD_R = 8;
const PAD_T = 16;
const PAD_B = 14;
const INNER_W = CHART_W - PAD_L - PAD_R;
const INNER_H = CHART_H - PAD_T - PAD_B;
const MOOD_AXIS_INSET = 8;
const MOOD_PLOT_H = INNER_H - MOOD_AXIS_INSET;
const MOOD_GRID_DASH = '2 2';

function moodToY(mood: number): number {
  return PAD_T + MOOD_PLOT_H * (1 - mood);
}

function xForDay(index: number): number {
  return PAD_L + (INNER_W * (2 * index + 1)) / 14;
}

const LibraryAnalysisContent: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const { t } = useAppSettings();
  const [weeklyMood, setWeeklyMood] = useState<(number | null)[]>(() =>
    Array.from({ length: 7 }, () => null),
  );
  const [emotionDetection, setEmotionDetection] = useState<EmotionDetectionResponse | null>(null);

  const refreshData = useCallback(async () => {
    const {
      fetchEmotionDetection,
      fetchWeeklyMoodTrend,
      getUserTimezone,
    } = await import('../../services/exploreService');
    const tz = getUserTimezone();
    const now = new Date();
    const weekStartKey = getDateKey(getWeekStartMonday(now));
    const [trend, emotions] = await Promise.all([
      fetchWeeklyMoodTrend(tz, weekStartKey),
      fetchEmotionDetection(),
    ]);
    console.log('!!!! emotions', emotions);

    setEmotionDetection(emotions);
    if (trend?.days?.length === 7) {
      setWeeklyMood(
        trend.days.map((d) =>
          d.has_data && d.score != null ? valenceToMoodChart01(d.score) : null,
        ),
      );
    } else {
      setWeeklyMood(Array.from({ length: 7 }, () => null));
    }
  }, []);

  useEffect(() => {
    refreshData();
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshData();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refreshData]);

  const weekdays = useMemo(
    () =>
      ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((d) =>
        t(`library.analysis.weekday.${d}`),
      ),
    [t],
  );

  const hasMoodDots = useMemo(() => weeklyMood.some((m) => m !== null), [weeklyMood]);

  const emotionPieSlices = useMemo(() => {
    const items = emotionDetection?.emotions;
    if (!items?.length) return [];
    return items.map((e, i) => ({
      emotion: translateInsightEmotionLabel(t, e.emotion),
      percentage: e.percentage,
      color: getEmotionColor(e.emotion, i),
    }));
  }, [emotionDetection, t]);

  const hasPieData = emotionPieSlices.length > 0;

  /** Solid fill so page gradient doesn’t darken the card; lighter than top-of-page navy (#02082f) */
  const cardClass = isDark
    ? 'rounded-[20px] bg-[#2563eb] border border-white/10 shadow-lg'
    : 'rounded-[20px] bg-white/95 border border-blue-200/60 shadow-md';

  const titleClass = isDark ? 'text-white' : 'text-gray-900';
  const chartTitleClass = isDark ? 'text-white' : 'text-gray-800';
  const mutedClass = isDark ? 'text-white/70' : 'text-gray-600';
  const gridStroke = isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)';
  const lineStroke = '#F5E6A8';

  return (
    <div className="space-y-8">
      {/* Mood Tracking */}
      <section>
        <h2 className={`text-xl font-semibold tracking-tight ${titleClass}`}>
          {t('library.analysis.moodTracking.title')}
        </h2>
        <p className="text-sm font-normal mt-2 leading-relaxed text-white">
          {t('library.analysis.moodTracking.subtitle')}
        </p>
        <div className={`mt-4 p-4 ${cardClass}`}>
          <h3 className={`text-sm font-semibold mb-3 ${chartTitleClass}`}>
            {t('library.analysis.moodTracking.chartTitle')}
          </h3>
          {!hasMoodDots && (
            <p className={`text-xs mb-2 ${mutedClass}`}>{t('library.analysis.noMoodData')}</p>
          )}
          <div className="relative mx-auto w-full max-w-[340px]">
            <div className="relative pl-8">
              <div className="relative aspect-[300/160] w-full">
                <svg
                  viewBox={`0 0 ${CHART_W} ${CHART_H}`}
                  className="absolute inset-0 size-full"
                  preserveAspectRatio="xMidYMid meet"
                  aria-label={t('library.analysis.moodTracking.title')}
                >
              {[0, 1, 2, 3, 4, 5].map((k) => {
                const y = PAD_T + (MOOD_PLOT_H * k) / 5;
                if (k === 5) {
                  return (
                    <line
                      key={k}
                      x1={PAD_L}
                      y1={y}
                      x2={CHART_W - PAD_R}
                      y2={y}
                      stroke={gridStroke}
                      strokeWidth={1.75}
                    />
                  );
                }
                return (
                  <line
                    key={k}
                    x1={PAD_L}
                    y1={y}
                    x2={CHART_W - PAD_R}
                    y2={y}
                    pathLength={100}
                    stroke={gridStroke}
                    strokeWidth={1}
                    strokeDasharray={MOOD_GRID_DASH}
                    strokeLinecap="round"
                  />
                );
              })}
              {(() => {
                const pts = weeklyMood
                  .map((m, i) => (m !== null ? `${xForDay(i)},${moodToY(m)}` : null))
                  .filter((p): p is string => p !== null);
                if (pts.length < 2) return null;
                return (
                  <polyline
                    fill="none"
                    stroke={lineStroke}
                    strokeWidth={1.5}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    points={pts.join(' ')}
                  />
                );
              })()}
              {weeklyMood.map((mood, i) =>
                mood === null ? null : (
                  <circle
                    key={i}
                    cx={xForDay(i)}
                    cy={moodToY(mood)}
                    r={5}
                    fill="white"
                    stroke={lineStroke}
                    strokeWidth={1.5}
                  />
                ),
              )}
            </svg>
              <div
                className="pointer-events-none absolute -left-8 top-0 h-full w-8"
                aria-hidden
              >
                <Smile
                  className={`absolute left-1/2 w-5 h-5 ${isDark ? 'text-white/90' : 'text-amber-600'}`}
                  strokeWidth={1.5}
                  style={{
                    top: `${(PAD_T / CHART_H) * 100}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                />
                <Frown
                  className={`absolute left-1/2 w-5 h-5 ${isDark ? 'text-white/90' : 'text-slate-500'}`}
                  strokeWidth={1.5}
                  style={{
                    top: `${((PAD_T + MOOD_PLOT_H) / CHART_H) * 100}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              </div>
              </div>
            <div
              className="relative mt-2 h-5 w-full"
              aria-label={t('library.analysis.moodTracking.chartTitle')}
            >
              {weekdays.map((wd, i) => (
                <span
                  key={wd}
                  className={`absolute top-0 min-w-0 max-w-[14%] truncate text-center text-[10px] font-medium leading-tight ${
                    isDark ? 'text-white/90' : 'text-gray-700'
                  }`}
                  style={{
                    left: `${((PAD_L + (INNER_W * (2 * i + 1)) / 14) / CHART_W) * 100}%`,
                    transform: 'translateX(-50%)',
                  }}
                >
                  {wd}
                </span>
              ))}
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Emotion Detection */}
      <section>
        <h2 className={`mb-3 text-xl font-semibold tracking-tight ${titleClass}`}>
          {t('library.analysis.recentEmotion.title')}
        </h2>
        <div className={`p-4 ${cardClass}`}>
          {!hasPieData && (
            <p className={`text-xs mb-3 ${mutedClass}`}>{t('library.analysis.noEmotionPieData')}</p>
          )}
          <div className="relative mx-auto w-[min(100%,65vw)] py-1">
            {hasPieData && (
              <div
                className="mx-auto flex justify-center"
                role="img"
                aria-label={t('library.analysis.recentEmotion.title')}
              >
                <EmotionPieChart emotions={emotionPieSlices} size={180} isDark={isDark} />
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LibraryAnalysisContent;
